// Puts emailed invoices into their clerk's list, and keeps one list per clerk.
//
// The fee slip screen is organised clerk-first, but an invoice that arrives by email carries
// no clerk: the intake only knows what the subject tag says, and clerks do not tag. So every
// emailed invoice from Meldrums, Greens or Parnell's sat in "From email – check type" and
// never appeared in that clerk's list, which is where outstanding fees are chased from.
//
// Clerks' accounts teams send from fixed addresses, so the sender says whose list an invoice
// belongs to. The subject usually names the client ("... in the matter of Thomas Towle") and
// often the barrister ("Invoice 00157164 for Anna Dearman in the matter of ...").
//
// This is read-time only: nothing stored is rewritten. A clerk chosen by hand on an emailed
// item is stored as clerk_name and always wins over the sender.
//
// The same clerk is also spelled differently by different sources - "Greens" and "Greens
// List", "Meldrums" and "Meldrum's List" - which split one clerk into two lists. clerkKey()
// reduces a name to the part that identifies the clerk, and unifyClerkNames() gives every
// row with the same key the same name.

const NOISE_WORDS = new Set([
  "list", "lists", "barristers", "barrister", "pty", "ltd", "limited",
  "clerks", "clerk", "clerking", "chambers", "accounts", "the", "and",
]);

// "Chapman's List Barristers Pty Ltd" -> "chapman"; "Greens" and "Greens List" -> "green";
// "Holmes' List" and "Holmes List Barristers" -> "holme". Only ever compared with itself,
// so an odd-looking key does not matter, only that spellings of one clerk agree.
export function clerkKey(name = "") {
  return String(name)
    .toLowerCase()
    .replace(/[’']/g, "")
    .split(/[^a-z0-9]+/)
    .filter((word) => word && !NOISE_WORDS.has(word))
    .map((word) => (word.length > 3 ? word.replace(/s$/, "") : word))
    .join("");
}

// Senders that are a clerk's accounts team. The name is what the list is called when no
// statement has given it a name of its own; when one has, unifyClerkNames() uses that.
export const CLERK_SENDERS = [
  { name: "Meldrums", test: (address) => /^meldrumaccounts\d*@vicbar\.com\.au$/.test(address) },
  { name: "Holmes List Barristers", test: (address) => /^holmesaccounts\d*@vicbar\.com\.au$/.test(address) },
  { name: "Chapman's List Barristers Pty Ltd", test: (address) => /^chapmansaccounts\d*@vicbar\.com\.au$/.test(address) },
  { name: "Dever's List", test: (address) => /^deveraccounts\d*@vicbar\.com\.au$/.test(address) },
  { name: "Greens List", test: (address) => address.endsWith("@greenslist.com.au") },
  { name: "Parnell's Barristers Pty Ltd", test: (address) => address.endsWith("@parnellsbarristers.com.au") },
  { name: "Foley's List Pty Ltd", test: (address) => address.endsWith("@foleys.com.au") },
  { name: "Svenson Barristers", test: (address) => address.endsWith("@svenson.com.au") },
];

// "Meldrum Accounts (2) <meldrumaccounts2@vicbar.com.au>" -> "meldrumaccounts2@vicbar.com.au"
export function senderAddress(sender = "") {
  const text = String(sender || "").trim();
  const angled = text.match(/<([^>]+)>/);
  return (angled ? angled[1] : text).trim().toLowerCase();
}

export function clerkFromSender(sender = "") {
  const address = senderAddress(sender);
  if (!address.includes("@")) return null;
  return CLERK_SENDERS.find((clerk) => clerk.test(address))?.name || null;
}

// Staff forward clerks' invoices too, and then the sender says nothing: LACW's 159 emailed
// fee slips all came from @lacw.org.au and had to be allocated by hand (Emma, 3 Nov 2026).
// Two things still name the clerk, and they are read in this order:
//
//   - the clerk's name in the subject or file name ("FW: Matter of Jane Citizen Holmes List
//     Invoice 06099001"). "List" or "Barristers" must follow it, because a client can be
//     called Holmes or Young;
//   - the invoice number. Each clerk numbers its invoices in its own run. These runs were
//     read off every invoice a clerk's own address had sent (3 Nov 2026), and gave no wrong
//     clerk across the 393 of those invoices they recognise.
//
// A number only counts written in full, with its leading zeros ("00169001"): that is how the
// clerks' system writes them, and it is what separates Holmes' 00779001 from a Greens number.
// A run that moves on simply stops matching, which leaves the item for a person to allocate.
const CLERK_NAMED = [
  { name: "Meldrums", test: /\bmeldrum[’']?s?\s+(?:list|barristers)\b/i },
  { name: "Holmes List Barristers", test: /\bholmes[’']?\s+(?:list|barristers)\b/i },
  { name: "Chapman's List Barristers Pty Ltd", test: /\bchapman[’']?s?\s+(?:list|barristers)\b/i },
  { name: "Dever's List", test: /\bdever[’']?s?\s+(?:list|barristers)\b/i },
  { name: "Greens List", test: /\bgreens?[’']?\s+(?:list|barristers)\b/i },
  { name: "Parnell's Barristers Pty Ltd", test: /\bparnell[’']?s?\s+(?:list|barristers)\b/i },
  { name: "Foley's List Pty Ltd", test: /\bfoley[’']?s?\s+(?:list|barristers)\b/i },
  { name: "Svenson Barristers", test: /\bsvenson[’']?s?\s+(?:list|barristers)\b/i },
  { name: "Patterson's List", test: /\bpatterson[’']?s?\s+(?:list|barristers)\b/i },
  { name: "Young's List", test: /\byoung[’']?s?\s+(?:list|barristers)\b/i },
  { name: "Lennon's List", test: /\blennon[’']?s?\s+(?:list|barristers)\b/i },
  { name: "Howells' List", test: /\bhowells?[’']?\s+(?:list|barristers)\b/i },
  { name: "List A", test: /\blist\s+a\s+barristers\b/i },
  { name: "List G", test: /\blist\s+g\s+barristers\b/i },
];

// Greens number differently: "INV-799001" in the file name or subject. Other suppliers write
// "INV-" too, but with shorter numbers ("INV-1311", "INV-12800").
const GREENS_INVOICE = /\bINV-[5-9]\d{5}\b/i;

const CLERK_INVOICE_RUNS = [
  { name: "Foley's List Pty Ltd", test: /^01\d{6}$/ },
  { name: "Holmes List Barristers", test: /^(?:060\d{5}|007[4-9]\d{4})$/ },
  { name: "Meldrums", test: /^00(?:59|6\d)\d{4}$/ },
  { name: "Dever's List", test: /^009\d{5}$/ },
  { name: "Parnell's Barristers Pty Ltd", test: /^001[3-9]\d{4}$/ },
  { name: "Svenson Barristers", test: /^001[01]\d{4}$/ },
  { name: "Chapman's List Barristers Pty Ltd", test: /^000[6-9]\d{4}$/ },
];

function clerkFromInvoiceNumber(invoiceRef = "", text = "") {
  if (GREENS_INVOICE.test(text)) return "Greens List";
  const digits = String(invoiceRef || "").replace(/\D/g, "");
  if (!digits) return null;
  // The stored number has lost its leading zeros, so they are looked for where it was read.
  const written = digits.padStart(8, "0");
  if (!text.includes(written)) return null;
  return CLERK_INVOICE_RUNS.find((run) => run.test.test(written))?.name || null;
}

// The clerk an emailed item belongs to, and how that is known. from: "sender", "subject" or
// "invoice". Null when nothing names one.
export function clerkFromEmail({ sender = "", subject = "", file_name: fileName = "", invoice_ref: invoiceRef = "" } = {}) {
  const fromSender = clerkFromSender(sender);
  if (fromSender) return { name: fromSender, from: "sender" };
  const text = `${fileName || ""} | ${subject || ""}`;
  const named = CLERK_NAMED.find((clerk) => clerk.test.test(text));
  if (named) return { name: named.name, from: "subject" };
  const numbered = clerkFromInvoiceNumber(invoiceRef, text);
  return numbered ? { name: numbered, from: "invoice" } : null;
}

const TIDY_TAIL = /\s*(?:[|(\[].*|-\s*(?:action required|urgent|overdue).*)$/i;
const tidyName = (value = "") => String(value).replace(TIDY_TAIL, "").replace(/[.,;:]+$/, "").trim();

// "FW: Overdue Invoice 00600448 in the matter of Thomas Towle" -> "Thomas Towle"
export function clientFromSubject(subject = "") {
  const text = String(subject || "");
  const match = text.match(/\b(?:in\s+)?the\s+matter\s+of\s+(?:R\s+v\.?\s+)?(.+)$/i)
    || text.match(/\bmatter\s*:\s*(?:R\s+v\.?\s+)?([^-|]+)/i);
  const name = match ? tidyName(match[1]) : "";
  return name.length >= 3 && name.length <= 80 ? name : "";
}

// "Invoice 00157164 for Anna Dearman in the matter of ..." -> "Anna Dearman"
// "Overdue Invoice of Ms Emily Allan in the matter of ..." -> "Emily Allan"
export function counselFromSubject(subject = "") {
  const match = String(subject || "").match(
    /\b(?:for|of)\s+(?:(?:Ms|Mr|Mrs|Miss|Dr)\.?\s+)?([A-Z][A-Za-z'’-]+(?:\s+[A-Z][A-Za-z'’-]+){0,3})\s+in\s+the\s+matter\s+of\b/,
  );
  return match ? match[1].trim() : "";
}

// Gives every row whose clerk names share a key the same name. A name that came off a clerk
// statement is preferred, since that is what the clerk calls itself; otherwise the most used
// spelling wins. Rows are returned as new objects; the input is not changed.
export function unifyClerkNames(rows = []) {
  const tally = new Map();
  for (const row of rows) {
    const name = String(row?.clerkName || "").trim();
    const key = clerkKey(name);
    if (!key) continue;
    if (!tally.has(key)) tally.set(key, new Map());
    const counts = tally.get(key);
    const weight = row.source === "clerk_statement" ? 1000 : 1;
    counts.set(name, (counts.get(name) || 0) + weight);
  }
  const chosen = new Map(
    [...tally.entries()].map(([key, counts]) => [
      key,
      [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0][0],
    ]),
  );
  return rows.map((row) => {
    const name = chosen.get(clerkKey(row?.clerkName || ""));
    return name && name !== row.clerkName ? { ...row, clerkName: name } : row;
  });
}
